import { useEffect, useId, useRef, useState } from 'react';
import { RADIO_STATIONS } from '../audio/radioStations';
import { useRadio, type RadioPlayer } from '../audio/radio';
import { radioLampState, radioStatusText } from '../audio/radioStatus';
import { soundFromSlider, type Settings } from './settings';

/** Keep playback mounted; only the phone presentation opens and closes. */
export function PersistentAudioControls(props: Parameters<typeof RadioControls>[0]) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      root.current?.querySelector('button')?.focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape, true);
    };
  }, [open]);
  return (
    <div className="persistent-audio" ref={root} data-open={open}>
      <button
        className="audio-toggle"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
      >
        Sound
      </button>
      <div id={id} className="audio-panel">
        <RadioControls {...props} />
      </div>
    </div>
  );
}

export function RadioControls({
  radio,
  compact = false,
  sound,
  onSoundChange,
}: {
  radio: RadioPlayer;
  compact?: boolean;
  sound: Settings['sound'];
  onSoundChange: (sound: Settings['sound']) => void;
}) {
  const state = useRadio(radio);
  const soundVolume = sound.muted ? 0 : sound.volume;
  const lamp = radioLampState(state.status);
  const selectedOut = state.selected !== null && radio.isOutOfOrder(state.selected);
  return (
    <div className={compact ? 'radio-controls settings-radio' : 'radio-controls'}>
      {compact && <span className="menu-label">Radio</span>}
      <div className="radio-buttons" role="group" aria-label="Radio channel">
        <button
          type="button"
          className={`radio-button ${state.selected === null ? 'selected' : ''}`}
          aria-pressed={state.selected === null}
          title="Radio off"
          onClick={() => void radio.select(null)}
        >
          OFF
        </button>
        {RADIO_STATIONS.map((station) => {
          const out = radio.isOutOfOrder(station.id);
          const selected = state.selected === station.id;
          return (
            <button
              type="button"
              key={station.id}
              className={`radio-button ${selected ? 'selected' : ''} ${out ? 'out' : ''} ${
                selected && lamp ? lamp : ''
              }`}
              aria-label={`${station.name}${out ? ', out of order' : ''}`}
              aria-pressed={state.selected === station.id}
              aria-disabled={out}
              title={station.name}
              onClick={() => void radio.select(station.id)}
            >
              <span>{station.label}</span>
              {out && <small>out of order</small>}
            </button>
          );
        })}
      </div>
      <p
        className={`radio-status ${lamp === 'trouble' ? 'trouble' : ''}`}
        aria-live="polite"
        data-testid="radio-status"
      >
        {radioStatusText(state, selectedOut)}
      </p>
      <label className={`audio-volume ${state.volume === 0 || sound.muted ? 'muted' : ''}`}>
        <span className="audio-volume-label">{compact ? 'Music' : 'MUSIC'}</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={state.volume}
          aria-label="Music volume"
          aria-valuetext={`${Math.round(state.volume * 100)} percent`}
          onChange={(event) => radio.setVolume(Number(event.target.value))}
        />
        {compact && <span className="slider-value">{Math.round(state.volume * 100)} %</span>}
      </label>
      <label className={`audio-volume ${soundVolume === 0 ? 'muted' : ''}`}>
        <span className="audio-volume-label">{compact ? 'Sound' : 'SOUND'}</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={soundVolume}
          aria-label="Sound volume"
          aria-valuetext={`${Math.round(soundVolume * 100)} percent`}
          onChange={(event) => onSoundChange(soundFromSlider(Number(event.target.value)))}
        />
        {compact && <span className="slider-value">{Math.round(soundVolume * 100)} %</span>}
      </label>
    </div>
  );
}
